import React from 'react';

/* Visuals for the hero demo. Design, Analyze and Test are generated images
   of the DRV-400 drive with code-drawn label overlays; Learn is a real data
   chart drawn in SVG. SVG text uses var(--t-sm) so the page keeps its three
   type sizes. */

const INK = 'var(--wb-ink)';
const MUTED = 'var(--wb-muted)';
const ORANGE = 'var(--wb-orange)';
const PAPER = 'var(--wb-paper)';
const mono = "'JetBrains Mono', ui-monospace, 'SF Mono', Menlo, Consolas, monospace";

/* ── image frames (Design, Analyze, Test) ─────────────────────────────────
   Generated images of the DRV-400 drive, 1200×625 WebP in /public/hero. Each
   carries an SVG overlay in the frame's own 480×250 space, so labels, part
   IDs and numbers are real text that matches the walk exactly. Labels sit in
   the image's empty right margin (kept to ~15 characters so they fit at
   every frame width); a paper halo keeps them legible. */
const Mark = ({ at, to, text, color = INK, anchor = 'start' }) => (
  <g>
    <path d={`M${at[0]} ${at[1]} L${to[0]} ${to[1]}`} stroke={color} strokeWidth="0.9" fill="none" />
    <circle cx={at[0]} cy={at[1]} r="2.4" fill={color} stroke={PAPER} strokeWidth="1" />
    <text
      x={to[0] + (anchor === 'start' ? 4 : -4)} y={to[1] + 4} textAnchor={anchor}
      style={{ fontSize: 'var(--t-sm)', fontFamily: mono, fill: color, stroke: '#fbf7ee', strokeWidth: 4, paintOrder: 'stroke', strokeLinejoin: 'round' }}
    >
      {text}
    </text>
  </g>
);
const Note = ({ x, y, text, color = MUTED }) => (
  <text x={x} y={y} style={{ fontSize: 'var(--t-sm)', fontFamily: mono, fill: color, stroke: '#fbf7ee', strokeWidth: 4, paintOrder: 'stroke' }}>{text}</text>
);

const ImageFrame = ({ src, alt, children }) => (
  <div className="wb-light" style={{ position: 'relative', width: '100%', height: '100%', background: '#fbf7ee' }}>
    <img src={src} alt={alt} width={1200} height={625} decoding="async" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain' }} />
    <svg viewBox="0 0 480 250" width="100%" height="100%" aria-hidden style={{ position: 'absolute', inset: 0 }}>{children}</svg>
  </div>
);

export const HERO_IMAGES = ['/hero/design-cad.webp', '/hero/analyze-fea.webp', '/hero/test-rig.webp'];

/* Design: the CAD view. */
export const CadDrawing = () => (
  <ImageFrame src={HERO_IMAGES[0]} alt="3D CAD view of the DRV-400 drive module, Rev A as proposed: motor, gearbox housing, finned controller pocket (highlighted) and 200 mm wheel.">
    <Mark at={[46, 90]} to={[24, 196]} text="MOT-410 motor" />
    <Mark at={[196, 44]} to={[350, 34]} text="CTL-460 · fins" color={ORANGE} />
    <Mark at={[250, 150]} to={[350, 84]} text="GBX-420 gearbox" />
    <Mark at={[323, 150]} to={[350, 134]} text="WHL-430 · Ø200" />
    <Note x={354} y={226} text="rev A · draft" />
  </ImageFrame>
);

/* Analyze: FEA, seen through a wireframe of the same drive. */
const BANDS = ['#c1321f', '#e0772a', '#e6b33a', '#8fbf5a', '#4fa3a5', '#3d6fb0'];
export const FeaDrawing = () => (
  <ImageFrame src={HERO_IMAGES[1]} alt="FEA of the DRV-400 drive seen through a wireframe of the housing: gears coloured by contact stress, peaking at 780 MPa on the GR-421 first-stage pinion.">
    <Mark at={[126, 78]} to={[350, 30]} text="max 780 MPa" />
    <Note x={354} y={50} text="GR-421 pinion" color={INK} />
    <Note x={354} y={84} text="stress, MPa" />
    {['780', '650', '520', '390', '260', '130'].map((v, i) => (
      <g key={v}>
        <rect x="354" y={92 + i * 19} width="14" height="16" fill={BANDS[i]} />
        <Note x={374} y={104 + i * 19} text={v} color={INK} />
      </g>
    ))}
    <Note x={354} y={226} text="load 25 N·m" />
  </ImageFrame>
);

/* Test: Rev A on the life-test rig. */
export const RigRender = () => (
  <ImageFrame src={HERO_IMAGES[2]} alt="Render of Rev A on the life-test rig: the drive's wheel runs on a roller drum with raised strips that replay warehouse floor joints, under a 300 kg load block.">
    <Mark at={[172, 22]} to={[346, 40]} text="300 kg load" />
    <Mark at={[178, 178]} to={[346, 150]} text="floor replay" />
    <Mark at={[222, 108]} to={[346, 95]} text="DRV-400 rev A" />
    <Note x={336} y={226} text="TR-310 life test" />
  </ImageFrame>
);

/* ── Learn: predicted vs measured ────────────────────────────────────── */
export const CorrelationChart = () => {
  const W = 480; const H = 250;
  /* A torque trace: steady running near 12 N·m with floor-joint spikes to 95. */
  const n = 90;
  const trace = Array.from({ length: n }, (_, i) => {
    const spike = [14, 33, 51, 70, 84].includes(i) ? 95 - (i % 3) * 6 : [15, 34, 52, 71, 85].includes(i) ? 40 : 0;
    return spike || 11 + 3 * Math.sin(i * 0.9) + 2 * Math.sin(i * 2.3);
  });
  const x0 = 40; const x1 = W - 16; const yTop = 20; const yBot = 118; const vMax = 100;
  const X = (i) => x0 + (i / (n - 1)) * (x1 - x0);
  const Y = (v) => yBot - (v / vMax) * (yBot - yTop);
  const lifeScale = (h) => (Math.log10(h) - 2) / (Math.log10(20000) - 2);
  const bars = [
    ['Predicted (assumed load)', 15000, MUTED],
    ['Recomputed (measured load)', 1050, ORANGE],
    ['Actual', 900, INK],
  ];
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height="100%" role="img" aria-label="Correlation: the measured torque trace spikes to 95 N·m at floor joints, far above the 25 N·m assumed. Gear life predicted 15,000 h; recomputed with the measured load 1,050 h; actual 900 h.">
      <text x={x0} y="12" style={{ fontSize: 'var(--t-sm)', fontFamily: mono, fill: MUTED }}>wheel torque, N·m · TR-310</text>
      <line x1={x0} y1={yBot} x2={x1} y2={yBot} stroke="var(--wb-line)" />
      {[0, 25, 50, 95].map((v) => <text key={v} x={x0 - 6} y={Y(v) + 4} textAnchor="end" style={{ fontSize: 'var(--t-sm)', fontFamily: mono, fill: MUTED }}>{v}</text>)}
      <line x1={x0} y1={Y(25)} x2={x1} y2={Y(25)} stroke={MUTED} strokeDasharray="5 4" />
      <polyline points={trace.map((v, i) => `${X(i).toFixed(1)},${Y(v).toFixed(1)}`).join(' ')} fill="none" stroke={ORANGE} strokeWidth="1.6" strokeLinejoin="round" />
      {/* legend, clear of the trace */}
      <line x1={x1 - 190} y1="8" x2={x1 - 170} y2="8" stroke={ORANGE} strokeWidth="1.6" />
      <text x={x1 - 166} y="12" style={{ fontSize: 'var(--t-sm)', fontFamily: mono, fill: ORANGE }}>measured</text>
      <line x1={x1 - 86} y1="8" x2={x1 - 66} y2="8" stroke={MUTED} strokeDasharray="5 4" />
      <text x={x1 - 62} y="12" style={{ fontSize: 'var(--t-sm)', fontFamily: mono, fill: MUTED }}>assumed</text>
      {/* gear life, log scale */}
      {bars.map(([label, h, color], i) => {
        const y = 146 + i * 34;
        const w = lifeScale(h) * (x1 - x0 - 150);
        return (
          <g key={label}>
            <text x={x0} y={y + 4} style={{ fontSize: 'var(--t-sm)', fontFamily: mono, fill: INK }}>{label}</text>
            <rect x={x0} y={y + 10} width={w} height="10" fill={color} rx="2" />
            <text x={x0 + w + 6} y={y + 19} style={{ fontSize: 'var(--t-sm)', fontFamily: mono, fill: color }}>{h.toLocaleString('en-US')} h</text>
          </g>
        );
      })}
    </svg>
  );
};
